// Admin overview dashboard. The mission of KIDO Smart VPN is to capture and
// classify advertising traffic with a particular focus on legal vs illegal
// gambling exposure in Japan. This page is a single-screen executive scan of
// that mission — split between widgets visible to both roles (sasadmin +
// sys-operator) and an extra "operational pulse" row that only sasadmin sees.
//
// Member management (codes table, lifecycle, APK templates, QUIC) lives at
// /admin/members. Deep-dive analytics live under /admin/research,
// /admin/research/activity, /admin/gambling-analysis, /admin/system-monitor.

import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Eye, Shield, AlertTriangle, Users, Brain, Cpu, Activity, Database,
  BarChart3, TrendingUp, RefreshCw, ArrowUpRight, Calendar, Layers,
} from 'lucide-react';
import {
  PieChart, Pie, Cell, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { authAPI, researchAPI, adminAPI } from '../services/api';
import type {
  ResearchOverview, LegalStatusDaily, PlatformComparison, AdvertiserProfile,
  TemporalHeatmapCell, ParticipationData, SystemOverview, LifecycleSummary,
} from '../types';
import DateRangePresets from '../components/DateRangePresets';
import KpiCard from '../components/KpiCard';
import ErrorBoundary from '../components/ErrorBoundary';
import CountryFilter, { type Country } from '../components/CountryFilter';
import { GAMBLING_COLORS, LEGAL_GAMBLING_CATEGORIES, ILLEGAL_GAMBLING_CATEGORIES, GRAY_ZONE_GAMBLING_CATEGORIES, PLATFORM_EMOJI } from '../lib/chartColors';

type AdminRole = 'sasadmin' | 'sys-operator';

const fmt = (v: number | string | null | undefined): string => {
  if (v === null || v === undefined) return '-';
  const n = typeof v === 'string' ? Number(v) : v;
  return isNaN(n) ? '-' : n.toLocaleString();
};
const pct = (a: number, b: number): string => (b > 0 ? `${(100 * a / b).toFixed(1)}%` : '-');

function isoDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
}
function todayIso(): string { return new Date().toISOString().split('T')[0]; }

const LEGAL_COLOR = '#22c55e';
const ILLEGAL_COLOR = '#ef4444';
const GRAY_ZONE_COLOR = '#f59e0b';

export default function AdminDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [role, setRole] = useState<AdminRole>('sasadmin');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Default scope: last 7 days. Section A's overview is lifetime so it ignores
  // the scope; B/C/D refetch when scope changes.
  const [dateFrom, setDateFrom] = useState(() => isoDaysAgo(6));
  const [dateTo, setDateTo] = useState(() => todayIso());
  const [country, setCountry] = useState<Country>(() => {
    const v = searchParams.get('country');
    return v === 'JP' || v === 'VN' ? v : 'all';
  });

  // Public data (both roles)
  const [overview, setOverview] = useState<ResearchOverview | null>(null);
  const [legalStatus, setLegalStatus] = useState<LegalStatusDaily[]>([]);
  const [platformComparison, setPlatformComparison] = useState<PlatformComparison[]>([]);
  const [advertisers, setAdvertisers] = useState<AdvertiserProfile[]>([]);
  const [heatmap, setHeatmap] = useState<TemporalHeatmapCell[]>([]);
  const [participation, setParticipation] = useState<ParticipationData | null>(null);

  // Sasadmin-only data
  const [systemOverview, setSystemOverview] = useState<SystemOverview | null>(null);
  const [lifecycle, setLifecycle] = useState<LifecycleSummary | null>(null);
  const [classifierAccuracy, setClassifierAccuracy] = useState<{ avgConfidence: number | null; manualOverrides: number; total: number } | null>(null);

  const isSasadmin = role === 'sasadmin';

  // ---- Data loading ----

  const loadPublic = useCallback(async () => {
    const opts = { dateFrom, dateTo, country };
    const results = await Promise.allSettled([
      researchAPI.getOverview({ country }),
      researchAPI.getLegalStatus(opts),
      researchAPI.getPlatformComparison(opts),
      researchAPI.getAdvertiserProfiles(opts),
      researchAPI.getTemporalHeatmap({ ...opts, gamblingOnly: true }),
      researchAPI.getParticipation({ country }),
    ]);
    if (results[0].status === 'fulfilled') setOverview(results[0].value);
    if (results[1].status === 'fulfilled') setLegalStatus(results[1].value);
    if (results[2].status === 'fulfilled') setPlatformComparison(results[2].value);
    if (results[3].status === 'fulfilled') setAdvertisers(results[3].value);
    if (results[4].status === 'fulfilled') setHeatmap(results[4].value);
    if (results[5].status === 'fulfilled') setParticipation(results[5].value);
    results.forEach((r, i) => {
      if (r.status === 'rejected') console.error(`Dashboard public fetch ${i} failed`, r.reason);
    });
  }, [dateFrom, dateTo, country]);

  const loadSasadmin = useCallback(async () => {
    const results = await Promise.allSettled([
      adminAPI.getSystemOverview(),
      adminAPI.getLifecycleSummary(),
      researchAPI.getClassificationAccuracy(),
    ]);
    if (results[0].status === 'fulfilled') setSystemOverview(results[0].value);
    if (results[1].status === 'fulfilled') setLifecycle(results[1].value);
    if (results[2].status === 'fulfilled') {
      const raw = results[2].value as { confidenceDistribution?: { confidence: number; count: number }[]; sourceDistribution?: { source: string; count: number }[]; totals?: { total: number; manualCount: number } } | null;
      const totals = raw?.totals ?? { total: 0, manualCount: 0 };
      const dist = raw?.confidenceDistribution ?? [];
      const totalConf = dist.reduce((s, b) => s + b.count, 0);
      const weighted = dist.reduce((s, b) => s + (b.confidence * b.count), 0);
      setClassifierAccuracy({
        avgConfidence: totalConf > 0 ? weighted / totalConf : null,
        manualOverrides: totals.manualCount,
        total: totals.total,
      });
    }
    results.forEach((r, i) => {
      if (r.status === 'rejected') console.error(`Dashboard sasadmin fetch ${i} failed`, r.reason);
    });
  }, []);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await loadPublic();
      if (isSasadmin) await loadSasadmin();
    } finally {
      setRefreshing(false);
    }
  }, [loadPublic, loadSasadmin, isSasadmin]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const auth = await authAPI.checkAdminAuth();
        if (cancelled) return;
        if (!auth.authenticated) { navigate('/admin'); return; }
        const r = (auth.user?.role ?? 'sasadmin') as AdminRole;
        setRole(r);
        await loadPublic();
        if (r === 'sasadmin') await loadSasadmin();
      } catch (err) {
        console.error('Dashboard init failed', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch windowed data when scope changes (skip the very first run, the
  // mount effect handles it).
  useEffect(() => {
    if (loading) return;
    void loadPublic();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, country]);

  // ---- Derived data ----

  const legalAggregate = useMemo(() => {
    let legal = 0, illegal = 0, grayZone = 0;
    for (const row of legalStatus) {
      const cat = row.category;
      const n = Number(row.adCount) || 0;
      if ((LEGAL_GAMBLING_CATEGORIES as readonly string[]).includes(cat)) legal += n;
      else if ((ILLEGAL_GAMBLING_CATEGORIES as readonly string[]).includes(cat)) illegal += n;
      else if ((GRAY_ZONE_GAMBLING_CATEGORIES as readonly string[]).includes(cat)) grayZone += n;
    }
    return { legal, illegal, grayZone, total: legal + illegal + grayZone };
  }, [legalStatus]);

  const dailyTrend = useMemo(() => {
    const byDate: Record<string, { date: string; legal: number; illegal: number; grayZone: number }> = {};
    for (const row of legalStatus) {
      if (!byDate[row.date]) byDate[row.date] = { date: row.date, legal: 0, illegal: 0, grayZone: 0 };
      const cat = row.category;
      const n = Number(row.adCount) || 0;
      if ((LEGAL_GAMBLING_CATEGORIES as readonly string[]).includes(cat)) byDate[row.date].legal += n;
      else if ((ILLEGAL_GAMBLING_CATEGORIES as readonly string[]).includes(cat)) byDate[row.date].illegal += n;
      else if ((GRAY_ZONE_GAMBLING_CATEGORIES as readonly string[]).includes(cat)) byDate[row.date].grayZone += n;
    }
    return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
  }, [legalStatus]);

  const platformBars = useMemo(() => {
    return [...platformComparison]
      .filter(p => Number(p.totalAds) > 0)
      .map(p => ({
        platform: p.platform,
        emoji: PLATFORM_EMOJI[p.platform] ?? '•',
        // Postgres NUMERIC columns come back as strings via node-postgres, coerce.
        gamblingPct: Number(p.gamblingPct ?? 0),
        gamblingAds: Number(p.gamblingAds ?? 0),
        totalAds: Number(p.totalAds ?? 0),
      }))
      .sort((a, b) => b.gamblingPct - a.gamblingPct);
  }, [platformComparison]);

  const topAdvertisers = useMemo(() => {
    const seen: Record<string, { advertiser: string; impressions: number; reach: number; daysActive: number; category: string }> = {};
    for (const row of advertisers) {
      if (!row.category.startsWith('gambling_')) continue;
      const key = row.advertiser || row.domain || '(unknown)';
      const impressions = Number(row.impressions) || 0;
      const reach = Number(row.reach) || 0;
      const daysActive = Number(row.daysActive) || 0;
      if (!seen[key]) {
        seen[key] = { advertiser: key, impressions: 0, reach: 0, daysActive: 0, category: row.category };
      }
      seen[key].impressions += impressions;
      seen[key].reach = Math.max(seen[key].reach, reach);
      seen[key].daysActive = Math.max(seen[key].daysActive, daysActive);
    }
    return Object.values(seen)
      .sort((a, b) => b.reach - a.reach || b.impressions - a.impressions)
      .slice(0, 5);
  }, [advertisers]);

  const heatmapGrid = useMemo(() => {
    const grid: number[][] = Array.from({ length: 7 }, () => Array(24).fill(0));
    for (const cell of heatmap) {
      const d = Number(cell.dayOfWeek);
      const h = Number(cell.hourOfDay);
      if (d >= 0 && d < 7 && h >= 0 && h < 24) {
        grid[d][h] += Number(cell.adCount) || 0;
      }
    }
    return grid;
  }, [heatmap]);
  const heatmapMax = useMemo(() => Math.max(1, ...heatmapGrid.flat()), [heatmapGrid]);

  const participationSummary = participation?.summary ?? null;

  const pieData = useMemo(() => {
    type Bucket = 'legal' | 'illegal' | 'gray_zone';
    const arr: { name: string; value: number; color: string; bucket: Bucket }[] = [
      { name: t('dashboard.legal'), value: legalAggregate.legal, color: LEGAL_COLOR, bucket: 'legal' },
      { name: t('dashboard.illegal'), value: legalAggregate.illegal, color: ILLEGAL_COLOR, bucket: 'illegal' },
    ];
    if (legalAggregate.grayZone > 0) arr.push({ name: t('dashboard.grayZone'), value: legalAggregate.grayZone, color: GRAY_ZONE_COLOR, bucket: 'gray_zone' });
    return arr;
  }, [legalAggregate, t]);

  const dayLabels = [
    t('dashboard.sun'), t('dashboard.mon'), t('dashboard.tue'),
    t('dashboard.wed'), t('dashboard.thu'), t('dashboard.fri'), t('dashboard.sat'),
  ];

  // ---- Drill-down navigation ----

  // Forward the dashboard's active dateFrom/dateTo and country scope to every
  // detail page so counts on the destination match what the user just clicked.
  function buildQs(extra: Record<string, string | undefined> = {}): string {
    const p = new URLSearchParams();
    if (dateFrom) p.set('dateFrom', dateFrom);
    if (dateTo) p.set('dateTo', dateTo);
    p.set('country', country);
    for (const [k, v] of Object.entries(extra)) if (v) p.set(k, v);
    return p.toString();
  }

  function gotoGamblingDeepDive() {
    const qs = buildQs({});
    if (isSasadmin) navigate(`/admin/gambling-analysis?${qs}`);
    else navigate(`/admin/research?${buildQs({ tab: 'gambling-exposure' })}`);
  }

  function gotoIllegalGambling() {
    if (isSasadmin) {
      navigate(`/admin/captured-ads?${buildQs({ category: 'gambling_illegal' })}`);
    } else {
      navigate(`/admin/research?${buildQs({ tab: 'gambling-exposure', legalStatus: 'illegal' })}`);
    }
  }

  function gotoLegalStatus(bucket: 'legal' | 'illegal' | 'gray_zone') {
    // Sasadmin → CapturedAds with legal-status-style filter via category.
    // Sys-operator can't access /admin/captured-ads, so route to research.
    if (isSasadmin) {
      // Backend aliases pre-aggregate the multi-category filters server-side
      // (see admin.ts captured-ads handler).
      const cat =
        bucket === 'illegal' ? 'gambling_illegal'
        : bucket === 'legal' ? 'gambling_legal'
        : 'gambling_gray';
      navigate(`/admin/captured-ads?${buildQs({ category: cat })}`);
    } else {
      navigate(`/admin/research?${buildQs({ tab: 'gambling-exposure', legalStatus: bucket })}`);
    }
  }

  // ---- Render ----

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 mx-auto text-amber-500 animate-spin" />
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">{t('dashboard.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <BarChart3 className="w-7 h-7 text-amber-500" />
              {t('dashboard.title')}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('dashboard.subtitle')}</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <CountryFilter value={country} onChange={setCountry} />
            <DateRangePresets dateFrom={dateFrom} dateTo={dateTo} onChange={(f, to) => { setDateFrom(f); setDateTo(to); }} showSummary={false} />
            <button
              onClick={refresh}
              disabled={refreshing}
              className="flex items-center gap-2 px-3 py-1.5 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              {t('dashboard.refresh')}
            </button>
          </div>
        </div>

        {/* === Section A: Headline KPIs (lifetime, both roles) === */}
        <section>
          <p className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">{t('dashboard.sectionA')}</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <KpiCard
              label={t('dashboard.totalAds')}
              value={fmt(overview?.totalAds)}
              sub={t('dashboard.lifetime')}
              icon={Eye}
              color="border-blue-500"
              onClick={() => navigate(`/admin/research?${buildQs({})}`)}
            />
            <KpiCard
              label={t('dashboard.gamblingAds')}
              value={fmt(overview?.gamblingAds)}
              sub={overview ? `${pct(overview.gamblingAds, overview.totalAds)} ${t('dashboard.ofTotal')}` : ''}
              icon={Shield}
              color="border-amber-500"
              onClick={gotoGamblingDeepDive}
            />
            <KpiCard
              label={t('dashboard.illegalGambling')}
              value={fmt(overview?.illegalGamblingAds)}
              sub={t('dashboard.gamblingOnlineCategory')}
              icon={AlertTriangle}
              color="border-red-500"
              onClick={gotoIllegalGambling}
            />
            <KpiCard
              label={t('dashboard.activeVolunteers')}
              value={`${fmt(overview?.activeVolunteers7d)} / ${fmt(overview?.totalVolunteers)}`}
              sub={t('dashboard.activeIn7d')}
              icon={Users}
              color="border-teal-500"
              onClick={() => navigate(`/admin/research/activity?${buildQs({})}`)}
            />
            <KpiCard
              label={t('dashboard.classification')}
              value={overview ? `${pct(overview.classifiedAds, overview.totalAds)}` : '-'}
              sub={overview ? `${fmt(overview.classifiedAds)} ${t('dashboard.classified')}` : ''}
              icon={Brain}
              color="border-purple-500"
              onClick={() => isSasadmin ? navigate('/admin/classifier-settings') : navigate(`/admin/research?${buildQs({ tab: 'accuracy' })}`)}
            />
          </div>
          {overview?.dataStartDate && overview?.dataEndDate && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {t('dashboard.dataSpan', { from: overview.dataStartDate, to: overview.dataEndDate })}
            </p>
          )}
        </section>

        <ErrorBoundary>
        {/* === Section B: Gambling pulse (legal vs illegal) === */}
        <section>
          <p className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">{t('dashboard.sectionB')}</p>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* B.1 Pie */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3 flex items-center gap-2">
                <Shield className="w-4 h-4 text-amber-500" /> {t('dashboard.legalVsIllegal')}
              </h3>
              {legalAggregate.total === 0 ? (
                <p className="text-center text-gray-400 dark:text-gray-500 py-12 text-sm">{t('dashboard.noData')}</p>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={(entry: { name: string; value: number }) => `${entry.name}: ${entry.value}`}
                        onClick={(data: { bucket?: 'legal' | 'illegal' | 'gray_zone' }) => data.bucket && gotoLegalStatus(data.bucket)}
                        style={{ cursor: 'pointer' }}
                      >
                        {pieData.map((entry, idx) => (
                          <Cell key={idx} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '0.5rem', color: '#fff' }}
                        formatter={(value: number, name: string) => [fmt(value), name]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  {legalAggregate.total < 50 && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 text-center mt-2 flex items-center justify-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      {t('dashboard.smallSampleWarning', { count: legalAggregate.total })}
                    </p>
                  )}
                </>
              )}
            </div>

            {/* B.2 Daily trend */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 lg:col-span-2">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-amber-500" /> {t('dashboard.dailyTrend')}
              </h3>
              {dailyTrend.length === 0 ? (
                <p className="text-center text-gray-400 dark:text-gray-500 py-12 text-sm">{t('dashboard.noData')}</p>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={dailyTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                    <XAxis dataKey="date" stroke="#9ca3af" fontSize={11} />
                    <YAxis stroke="#9ca3af" fontSize={11} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '0.5rem', color: '#fff' }}
                    />
                    <Legend />
                    <Area type="monotone" dataKey="legal" stackId="1" stroke={LEGAL_COLOR} fill={LEGAL_COLOR} fillOpacity={0.5} name={t('dashboard.legal')} />
                    <Area type="monotone" dataKey="illegal" stackId="1" stroke={ILLEGAL_COLOR} fill={ILLEGAL_COLOR} fillOpacity={0.5} name={t('dashboard.illegal')} />
                    {legalAggregate.grayZone > 0 && (
                      <Area type="monotone" dataKey="grayZone" stackId="1" stroke={GRAY_ZONE_COLOR} fill={GRAY_ZONE_COLOR} fillOpacity={0.5} name={t('dashboard.grayZone')} />
                    )}
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </section>

        {/* === Section C: Where the gambling ads come from === */}
        <section>
          <p className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">{t('dashboard.sectionC')}</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* C.1 Platform breakdown */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3 flex items-center gap-2">
                <Layers className="w-4 h-4 text-sky-500" /> {t('dashboard.platformBreakdown')}
              </h3>
              {platformBars.length === 0 ? (
                <p className="text-center text-gray-400 dark:text-gray-500 py-12 text-sm">{t('dashboard.noData')}</p>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={platformBars} layout="vertical" margin={{ left: 24 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                    <XAxis type="number" stroke="#9ca3af" fontSize={11} unit="%" />
                    <YAxis dataKey="platform" type="category" stroke="#9ca3af" fontSize={11} width={90} tickFormatter={(p: string) => `${PLATFORM_EMOJI[p] ?? ''} ${p}`} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '0.5rem', color: '#fff' }}
                      formatter={(value: number | string, _name: string, props: { payload?: { gamblingAds?: number; totalAds?: number } }) => {
                        const p = props.payload;
                        const pctNum = Number(value);
                        const pctText = isNaN(pctNum) ? String(value) : `${pctNum.toFixed(1)}%`;
                        return [`${pctText} (${fmt(p?.gamblingAds)} / ${fmt(p?.totalAds)})`, t('dashboard.gamblingRate')];
                      }}
                    />
                    <Bar dataKey="gamblingPct" fill="#f59e0b" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* C.2 Top gambling advertisers */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3 flex items-center gap-2">
                <Shield className="w-4 h-4 text-red-500" /> {t('dashboard.topGamblingAdvertisers')}
              </h3>
              {topAdvertisers.length === 0 ? (
                <p className="text-center text-gray-400 dark:text-gray-500 py-12 text-sm">{t('dashboard.noData')}</p>
              ) : (
                <div className="space-y-2">
                  {topAdvertisers.map((adv, i) => {
                    const maxReach = topAdvertisers[0].reach || 1;
                    const widthPct = (adv.reach / maxReach) * 100;
                    const color = GAMBLING_COLORS[adv.category] ?? '#9ca3af';
                    return (
                      <div key={i}>
                        <div className="flex justify-between items-center text-xs mb-1">
                          <span className="font-semibold text-gray-700 dark:text-gray-200 truncate" title={adv.advertiser}>
                            {i + 1}. {adv.advertiser}
                          </span>
                          <span className="text-gray-500 dark:text-gray-400 whitespace-nowrap ml-2">
                            {fmt(adv.reach)} {t('dashboard.uniqueUsers')}
                          </span>
                        </div>
                        <div className="bg-gray-100 dark:bg-gray-700 rounded h-2 overflow-hidden">
                          <div className="h-full rounded transition-all" style={{ width: `${widthPct}%`, backgroundColor: color }} />
                        </div>
                        <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
                          {t(`dashboard.cat_${adv.category}`, adv.category)} · {fmt(adv.impressions)} {t('dashboard.impressions')} · {adv.daysActive}d
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* === Section D: Engagement & coverage === */}
        <section>
          <p className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">{t('dashboard.sectionD')}</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* D.1 Heatmap */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-purple-500" /> {t('dashboard.peakHours')}
              </h3>
              <div className="overflow-x-auto">
                <table className="text-[10px] border-separate border-spacing-0">
                  <thead>
                    <tr>
                      <th className="text-gray-400 font-normal w-8"></th>
                      {Array.from({ length: 24 }, (_, h) => (
                        <th key={h} className="text-gray-400 font-normal w-5 text-center">{h % 6 === 0 ? h : ''}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {heatmapGrid.map((row, di) => (
                      <tr key={di}>
                        <td className="text-gray-500 dark:text-gray-400 pr-1 font-medium">{dayLabels[di]}</td>
                        {row.map((value, hi) => {
                          const intensity = value / heatmapMax;
                          const bg = value === 0
                            ? 'rgba(156,163,175,0.1)'
                            : `rgba(239, 68, 68, ${0.15 + 0.7 * intensity})`;
                          return (
                            <td key={hi}
                              title={`${dayLabels[di]} ${hi}:00 — ${value} ${t('dashboard.gamblingAds').toLowerCase()}`}
                              style={{ backgroundColor: bg }}
                              className="w-5 h-5 border border-white dark:border-gray-800"
                            />
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-2">{t('dashboard.peakHoursHelp')}</p>
            </div>

            {/* D.2 Participation summary */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3 flex items-center gap-2">
                <Users className="w-4 h-4 text-teal-500" /> {t('dashboard.participation')}
              </h3>
              {!participationSummary ? (
                <p className="text-center text-gray-400 dark:text-gray-500 py-12 text-sm">{t('dashboard.noData')}</p>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 p-3">
                    <p className="text-xs text-emerald-700 dark:text-emerald-300">{t('dashboard.partActive')}</p>
                    <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{fmt(participationSummary.active)}</p>
                    <p className="text-[10px] text-emerald-600/70 dark:text-emerald-300/70">{t('dashboard.partActiveHelp')}</p>
                  </div>
                  <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 p-3">
                    <p className="text-xs text-amber-700 dark:text-amber-300">{t('dashboard.partStale')}</p>
                    <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">{fmt(participationSummary.stale)}</p>
                    <p className="text-[10px] text-amber-600/70 dark:text-amber-300/70">{t('dashboard.partStaleHelp')}</p>
                  </div>
                  <div className="rounded-lg bg-gray-100 dark:bg-gray-700/50 p-3">
                    <p className="text-xs text-gray-700 dark:text-gray-300">{t('dashboard.partNoData')}</p>
                    <p className="text-2xl font-bold text-gray-700 dark:text-gray-300">{fmt(participationSummary.noData)}</p>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400">{t('dashboard.partNoDataHelp')}</p>
                  </div>
                  <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 p-3">
                    <p className="text-xs text-blue-700 dark:text-blue-300">{t('dashboard.partTotal')}</p>
                    <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{fmt(participationSummary.total)}</p>
                    <p className="text-[10px] text-blue-600/70 dark:text-blue-300/70">{t('dashboard.partTotalHelp')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* === Section E: Operational pulse (sasadmin only) === */}
        {isSasadmin && (
          <section>
            <p className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">{t('dashboard.sectionE')}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* CPU / RAM / Disk */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => navigate('/admin/system-monitor')}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1"><Cpu className="w-4 h-4" /> {t('dashboard.systemLoad')}</p>
                  <ArrowUpRight className="w-4 h-4 text-gray-300" />
                </div>
                {systemOverview ? (
                  <div className="space-y-1.5">
                    <SystemBar label="CPU" value={Math.min(100, (systemOverview.systemMetrics.cpuLoad.load1m / Math.max(1, systemOverview.systemMetrics.cpuLoad.cpuCount ?? 1)) * 100)} />
                    <SystemBar label="RAM" value={systemOverview.systemMetrics.memory.usedPercent} />
                    <SystemBar label="DISK" value={systemOverview.systemMetrics.disk.usedPercent} />
                  </div>
                ) : <p className="text-xs text-gray-400">-</p>}
              </div>

              {/* Enricher queue */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => navigate('/admin/enrichment-monitor')}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1"><Activity className="w-4 h-4" /> {t('dashboard.enricher')}</p>
                  <ArrowUpRight className="w-4 h-4 text-gray-300" />
                </div>
                {systemOverview ? (
                  <>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{fmt(systemOverview.enricher.remaining)}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('dashboard.enricherPending')}</p>
                    <div className="mt-2 bg-gray-100 dark:bg-gray-700 rounded h-1.5 overflow-hidden">
                      <div className="h-full bg-emerald-500" style={{ width: `${systemOverview.enricher.progressPercent}%` }} />
                    </div>
                    <p className="text-[10px] text-gray-400 mt-1">{systemOverview.enricher.progressPercent.toFixed(1)}% {t('dashboard.enricherDone')}</p>
                  </>
                ) : <p className="text-xs text-gray-400">-</p>}
              </div>

              {/* Classifier accuracy */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => navigate('/admin/classifier-settings')}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1"><Brain className="w-4 h-4" /> {t('dashboard.classifierAccuracy')}</p>
                  <ArrowUpRight className="w-4 h-4 text-gray-300" />
                </div>
                {classifierAccuracy ? (
                  <>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {classifierAccuracy.avgConfidence !== null ? `${(classifierAccuracy.avgConfidence * 100).toFixed(1)}%` : '-'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('dashboard.avgConfidence')}</p>
                    <p className="text-[10px] text-gray-400 mt-1">{fmt(classifierAccuracy.manualOverrides)} {t('dashboard.manualOverrides')}</p>
                  </>
                ) : <p className="text-xs text-gray-400">-</p>}
              </div>

              {/* Code lifecycle */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => navigate('/admin/members')}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1"><Database className="w-4 h-4" /> {t('dashboard.codeLifecycle')}</p>
                  <ArrowUpRight className="w-4 h-4 text-gray-300" />
                </div>
                {lifecycle ? (
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                    <div><span className="text-gray-500">{t('dashboard.lifecycleIssued')}: </span><span className="font-bold text-gray-900 dark:text-white">{fmt(lifecycle.issued)}</span></div>
                    <div><span className="text-gray-500">{t('dashboard.lifecycleRegistered')}: </span><span className="font-bold text-gray-900 dark:text-white">{fmt(lifecycle.registered)}</span></div>
                    <div><span className="text-gray-500">{t('dashboard.lifecycleStartedUsing')}: </span><span className="font-bold text-gray-900 dark:text-white">{fmt(lifecycle.startedUsing)}</span></div>
                    <div><span className="text-gray-500">{t('dashboard.lifecycleActive')}: </span><span className="font-bold text-emerald-600 dark:text-emerald-400">{fmt(lifecycle.active)}</span></div>
                  </div>
                ) : <p className="text-xs text-gray-400">-</p>}
              </div>
            </div>
          </section>
        )}
        </ErrorBoundary>

        {/* === Section F: Quick actions === */}
        <section className="pt-2">
          <div className="flex flex-wrap gap-2 justify-center">
            <QuickLink to="/admin/research" label={t('dashboard.qaResearch')} icon={BarChart3} navigate={navigate} />
            <QuickLink to="/admin/research/activity" label={t('dashboard.qaActivity')} icon={TrendingUp} navigate={navigate} />
            {isSasadmin && <QuickLink to="/admin/members" label={t('dashboard.qaMembers')} icon={Users} navigate={navigate} />}
            {isSasadmin && <QuickLink to="/admin/captured-ads" label={t('dashboard.qaCapturedAds')} icon={Eye} navigate={navigate} />}
            {isSasadmin && <QuickLink to="/admin/gambling-analysis" label={t('dashboard.qaGambling')} icon={Shield} navigate={navigate} />}
            {isSasadmin && <QuickLink to="/admin/system-monitor" label={t('dashboard.qaSystem')} icon={Cpu} navigate={navigate} />}
          </div>
        </section>

      </div>
    </div>
  );
}

function SystemBar({ label, value }: { label: string; value: number }) {
  const v = Math.max(0, Math.min(100, value));
  const color = v >= 90 ? 'bg-red-500' : v >= 70 ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div>
      <div className="flex justify-between text-[10px] text-gray-500 dark:text-gray-400 mb-0.5">
        <span>{label}</span>
        <span>{v.toFixed(0)}%</span>
      </div>
      <div className="bg-gray-100 dark:bg-gray-700 rounded h-1.5 overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${v}%` }} />
      </div>
    </div>
  );
}

function QuickLink({ to, label, icon: Icon, navigate }: { to: string; label: string; icon: React.ComponentType<{ className?: string }>; navigate: (to: string) => void }) {
  return (
    <button
      onClick={() => navigate(to)}
      className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-200 transition-colors"
    >
      <Icon className="w-4 h-4" />
      {label}
      <ArrowUpRight className="w-3 h-3 text-gray-400" />
    </button>
  );
}
